import React, { useEffect } from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import EmergencyPanicButton from "./components/EmergencyPanicButton";
import HiddenModeDebug from "./components/HiddenModeDebug";
import Layout from "./components/Layout/Layout";
import ProtectedRoute from "./components/ProtectedRoute";
import { AuthProvider } from "./contexts/AuthContext";
import {
  HiddenModeProvider,
  useHiddenMode,
} from "./contexts/HiddenModeContext";
import { SecurityProvider } from "./contexts/SecurityContext";
import "./css/App.css";
import "./css/security.css";
import AddBooking from "./pages/AddBooking";
import AddBroker from "./pages/AddBroker";
import AddExpenses from "./pages/AddExpenses";
import AddIncome from "./pages/AddIncome";
import AddReceivableIncome from "./pages/AddReceivableIncome";
import AddOtherIncome from "./pages/AddOtherIncome";
import AddPartner from "./pages/AddPartner";
import AddPartnerIncome from "./pages/AddPartnerIncome";
import AddProject from "./pages/AddProject";
import AddRole from "./pages/AddRole";
import AddSystemUser from "./pages/AddSystemUser";
import AddUnit from "./pages/AddUnit";
import AuthorizationDashboard from "./pages/AuthorizationDashboard";
import BookingAuthorize from "./pages/BookingAuthorize";
import BookingDetails from "./pages/BookingDetails";
import BookingHistory from "./pages/BookingHistory";
import Bookings from "./pages/Bookings";
import BookingSummary from "./pages/BookingSummary";
import Brokers from "./pages/Brokers";
import CancelledBookings from "./pages/CancelledBookings";
import CancelReceivableIncome from "./pages/CancelReceivableIncome";
import CancelSummary from "./pages/CancelSummary";
import Dashboard from "./pages/Dashboard";
import EditBooking from "./pages/EditBooking";
import EditRole from "./pages/EditRole";
import Expenses from "./pages/Expenses";
import HiddenIncome from "./pages/HiddenIncome";
import HiddenIncomeAuthorize from "./pages/HiddenIncomeAuthorize";
import HistoricalDataUpload from "./pages/HistoricalDataUpload";
import HistoricalUpload from "./pages/HistoricalUpload";
import IncomePage from "./pages/Income";
import IncomeAuthorize from "./pages/IncomeAuthorize";
import Login from "./pages/Login";
import OtherIncomePage from "./pages/OtherIncome";
import PartnerLedger from "./pages/PartnerLedger";
import Partners from "./pages/Partners";
import Permissions from "./pages/Permissions";
import Project from "./pages/Project";
import ReturnCancelIncome from "./pages/ReturnCancelIncome";
import Roles from "./pages/Roles";
import SystemUsers from "./pages/SystemUsers";
import TransferredBookings from "./pages/TransferredBookings";
import Unauthorized from "./pages/Unauthorized";
import Units from "./pages/Units";
import UploadData from "./pages/UploadData";
import ViewRole from "./pages/ViewRole";
import UnitReport from "./pages/UnitReport";
import IncomeReport from "./pages/IncomeReport";
import FollowUpReport from "./pages/FollowUpReport";
import ReceivableReport from "./pages/ReceivableReport";
import CombinedIncomeReport from "./pages/CombinedIncomeReport";
import AdvancedFollowUpReport from "./pages/AdvancedFollowUpReport";

// Keyboard shortcut handler component
const KeyboardShortcutHandler: React.FC = () => {
  const { showHiddenItems, hideHiddenItems, isHiddenModeActive } =
    useHiddenMode();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Multiple ways to trigger hidden mode
      const isShowShortcut =
        // Original: Alt+Ctrl+Shift+6
        (event.ctrlKey &&
          event.shiftKey &&
          (event.key === "6" || event.keyCode === 54)) ||
        // Alternative: Alt+Ctrl+Shift+H
        (event.ctrlKey && event.shiftKey && event.key.toLowerCase() === "h") ||
        // Alternative: Alt+Ctrl+Shift+^ (for keyboards where 6 is dead key)
        (event.ctrlKey &&
          event.shiftKey &&
          (event.key === "^" || event.key === "Dead"));

      if (isShowShortcut && !isHiddenModeActive) {
        console.log("Hidden mode shortcut triggered - showing");
        event.preventDefault();
        event.stopPropagation();
        showHiddenItems();
      }

      // Hide with Escape key when hidden mode is active
      if (event.key === "Escape" && isHiddenModeActive) {
        // Don't hide if a modal is open (check for modal overlay in DOM)
        const hasOpenModal = document.querySelector('.modal-overlay, .fullscreen-modal-overlay');
        if (hasOpenModal) {
          console.log("Escape key ignored - modal is open");
          return; // Let the modal handle the ESC key
        }
        console.log("Escape key detected - hiding");
        event.preventDefault();
        hideHiddenItems();
      }

      // Hide with Alt+Ctrl+Shift+X
      if (
        event.altKey &&
        event.ctrlKey &&
        event.shiftKey &&
        event.key.toLowerCase() === "x" &&
        isHiddenModeActive
      ) {
        console.log("Hide shortcut detected - hiding");
        event.preventDefault();
        hideHiddenItems();
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      // Double Ctrl tap detection for hiding hidden mode - improved timing
      if (event.key === "Control" && isHiddenModeActive) {
        const now = Date.now();
        if (window.lastCtrlPress && now - window.lastCtrlPress < 800) {
          console.log("Double Ctrl tap detected - hiding");
          hideHiddenItems();
          window.lastCtrlPress = 0;
        } else {
          window.lastCtrlPress = now;
        }
      }
    };

    // Use capture phase to intercept events before other handlers
    document.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("keyup", handleKeyUp, true);

    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("keyup", handleKeyUp, true);
    };
  }, [showHiddenItems, hideHiddenItems, isHiddenModeActive]);

  return null;
};

// Extend Window interface for TypeScript
declare global {
  interface Window {
    lastCtrlPress: number;
  }
}

function App() {
  return (
    <AuthProvider>
      <SecurityProvider>
        <HiddenModeProvider>
          <KeyboardShortcutHandler />
          <Router>
            <div className="App">
              {/* Toast Notifications */}
              <ToastContainer position="top-right" autoClose={4000} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover />

              {/* Emergency Panic Button - Always available */}
              <EmergencyPanicButton />

              {/* Hidden Mode Debug - Only in development */}
              <HiddenModeDebug />

              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/unauthorized" element={<Unauthorized />} />

                {/* Protected routes with Layout */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Layout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Navigate to="/dashboard" replace />} />
                  <Route path="dashboard" element={<Dashboard />} />

                  {/* Project Management */}
                  <Route
                    path="projects"
                    element={
                      <ProtectedRoute requiredPermissions={["project.view"]}>
                        <Project />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="project/add"
                    element={
                      <ProtectedRoute requiredPermissions={["project.create"]}>
                        <AddProject />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="project/edit"
                    element={
                      <ProtectedRoute requiredPermissions={["project.edit"]}>
                        <AddProject />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="project/view"
                    element={
                      <ProtectedRoute requiredPermissions={["project.view"]}>
                        <AddProject />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="units"
                    element={
                      <ProtectedRoute requiredPermissions={["unit.view"]}>
                        <Units />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="unit/add"
                    element={
                      <ProtectedRoute requiredPermissions={["unit.create"]}>
                        <AddUnit />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="unit/edit"
                    element={
                      <ProtectedRoute requiredPermissions={["unit.edit"]}>
                        <AddUnit />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="unit/view"
                    element={
                      <ProtectedRoute requiredPermissions={["unit.view"]}>
                        <AddUnit />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="project/units"
                    element={
                      <ProtectedRoute requiredPermissions={["unit.view"]}>
                        <Units />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="project/unit/add"
                    element={
                      <ProtectedRoute requiredPermissions={["unit.create"]}>
                        <AddUnit />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="project/unit/edit"
                    element={
                      <ProtectedRoute requiredPermissions={["unit.edit"]}>
                        <AddUnit />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="project/unit/view"
                    element={
                      <ProtectedRoute requiredPermissions={["unit.view"]}>
                        <AddUnit />
                      </ProtectedRoute>
                    }
                  />

                  {/* Booking Management */}
                  <Route
                    path="bookings"
                    element={
                      <ProtectedRoute requiredPermissions={["booking.view"]}>
                        <Bookings />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="booking/details"
                    element={
                      <ProtectedRoute requiredPermissions={["booking.view"]}>
                        <BookingDetails />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="booking/add"
                    element={
                      <ProtectedRoute requiredPermissions={["booking.create"]}>
                        <AddBooking />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="booking/edit"
                    element={
                      <ProtectedRoute requiredPermissions={["booking.edit"]}>
                        <EditBooking />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="booking/view"
                    element={
                      <ProtectedRoute requiredPermissions={["booking.view"]}>
                        <EditBooking />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="booking/edit-legacy"
                    element={
                      <ProtectedRoute requiredPermissions={["booking.edit"]}>
                        <AddBooking />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="booking/edit"
                    element={
                      <ProtectedRoute requiredPermissions={["booking.edit"]}>
                        <EditBooking />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="booking/history"
                    element={
                      <ProtectedRoute requiredPermissions={["booking.view"]}>
                        <BookingHistory />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="booking/cancelled"
                    element={
                      <ProtectedRoute requiredPermissions={["booking.view"]}>
                        <CancelledBookings />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="return-cancel-income"
                    element={<ReturnCancelIncome />}
                  />
                  <Route
                    path="cancel-receivable-income"
                    element={<CancelReceivableIncome />}
                  />
                  <Route
                    path="booking/transferred"
                    element={
                      <ProtectedRoute requiredPermissions={["booking.view"]}>
                        <TransferredBookings />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="historical-data-upload"
                    element={
                      <ProtectedRoute requiredPermissions={["booking.create"]}>
                        <HistoricalDataUpload />
                      </ProtectedRoute>
                    }
                  />

                  {/* Partner Management */}
                  <Route
                    path="partners"
                    element={
                      <ProtectedRoute requiredPermissions={["partner.view"]}>
                        <Partners />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="partner/add"
                    element={
                      <ProtectedRoute requiredPermissions={["partner.create"]}>
                        <AddPartner />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="partner/edit"
                    element={
                      <ProtectedRoute requiredPermissions={["partner.edit"]}>
                        <AddPartner />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="partner/view"
                    element={
                      <ProtectedRoute requiredPermissions={["partner.view"]}>
                        <AddPartner />
                      </ProtectedRoute>
                    }
                  />

                  {/* Broker Management */}
                  <Route
                    path="brokers"
                    element={
                      <ProtectedRoute requiredPermissions={["broker.view"]}>
                        <Brokers />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="broker/add"
                    element={
                      <ProtectedRoute requiredPermissions={["broker.create"]}>
                        <AddBroker />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="broker/edit"
                    element={
                      <ProtectedRoute requiredPermissions={["broker.edit"]}>
                        <AddBroker />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="broker/view"
                    element={
                      <ProtectedRoute requiredPermissions={["broker.view"]}>
                        <AddBroker />
                      </ProtectedRoute>
                    }
                  />

                  {/* Financial Management */}
                  <Route
                    path="income"
                    element={
                      <ProtectedRoute requiredPermissions={["income.view"]}>
                        <IncomePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="income/add"
                    element={
                      <ProtectedRoute requiredPermissions={["income.create"]}>
                        <AddIncome />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="income/edit"
                    element={
                      <ProtectedRoute requiredPermissions={["income.edit"]}>
                        <AddIncome />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="income/view"
                    element={
                      <ProtectedRoute requiredPermissions={["income.view"]}>
                        <AddIncome />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="hidden-income"
                    element={
                      <ProtectedRoute requiredPermissions={["income.view"]}>
                        <HiddenIncome />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="add-receivable-income"
                    element={
                      <ProtectedRoute requiredPermissions={["income.view"]}>
                        <AddReceivableIncome />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="booking-summary"
                    element={
                      <ProtectedRoute
                        requiredPermissions={[
                          "*",
                          "superadmin",
                          "booking.view",
                        ]}
                      >
                        <BookingSummary />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="cancel-summary"
                    element={
                      <ProtectedRoute
                        requiredPermissions={[
                          "*",
                          "superadmin",
                          "booking.view",
                        ]}
                      >
                        <CancelSummary />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="upload-data"
                    element={
                      <ProtectedRoute requiredPermissions={["*", "superadmin"]}>
                        <UploadData />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="historical-upload"
                    element={
                      <ProtectedRoute requiredPermissions={["*", "superadmin"]}>
                        <HistoricalUpload />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="ledger/income"
                    element={
                      <ProtectedRoute requiredPermissions={["income.view"]}>
                        <IncomePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="ledger/add-income"
                    element={
                      <ProtectedRoute requiredPermissions={["income.create"]}>
                        <AddIncome />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="expenses"
                    element={
                      <ProtectedRoute requiredPermissions={["expense.view"]}>
                        <Expenses />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="expenses/add"
                    element={
                      <ProtectedRoute requiredPermissions={["expense.create"]}>
                        <AddExpenses />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="ledger/expenses"
                    element={
                      <ProtectedRoute requiredPermissions={["expense.view"]}>
                        <Expenses />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="ledger/expenses/add"
                    element={
                      <ProtectedRoute requiredPermissions={["expense.create"]}>
                        <AddExpenses />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="ledger/expenses/edit"
                    element={
                      <ProtectedRoute requiredPermissions={["expense.edit"]}>
                        <AddExpenses />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="other-income"
                    element={
                      <ProtectedRoute requiredPermissions={["income.view"]}>
                        <OtherIncomePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="other-income/add"
                    element={
                      <ProtectedRoute requiredPermissions={["income.create"]}>
                        <AddOtherIncome />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="ledger/other"
                    element={
                      <ProtectedRoute requiredPermissions={["income.view"]}>
                        <OtherIncomePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="ledger/other/add"
                    element={
                      <ProtectedRoute requiredPermissions={["income.create"]}>
                        <AddOtherIncome />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="ledger/other/edit"
                    element={
                      <ProtectedRoute requiredPermissions={["income.edit"]}>
                        <AddOtherIncome />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="ledger/other/view"
                    element={
                      <ProtectedRoute requiredPermissions={["income.view"]}>
                        <AddOtherIncome />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="partner-income"
                    element={
                      <ProtectedRoute
                        requiredPermissions={["partner.view", "income.view"]}
                        requireAll={true}
                      >
                        <PartnerLedger />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="partner-income/add"
                    element={
                      <ProtectedRoute
                        requiredPermissions={["partner.view", "income.create"]}
                        requireAll={true}
                      >
                        <AddPartnerIncome />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="partner-income/edit"
                    element={
                      <ProtectedRoute
                        requiredPermissions={["partner.view", "income.edit"]}
                        requireAll={true}
                      >
                        <AddPartnerIncome />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="partner-income/view"
                    element={
                      <ProtectedRoute
                        requiredPermissions={["partner.view", "income.view"]}
                        requireAll={true}
                      >
                        <AddPartnerIncome />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="ledger/partner"
                    element={
                      <ProtectedRoute
                        requiredPermissions={["partner.view", "income.view"]}
                        requireAll={true}
                      >
                        <PartnerLedger />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="ledger/partner/add"
                    element={
                      <ProtectedRoute
                        requiredPermissions={["partner.view", "income.create"]}
                        requireAll={true}
                      >
                        <AddPartnerIncome />
                      </ProtectedRoute>
                    }
                  />

                  {/* Authorization/Approval Routes */}
                  <Route
                    path="approval/dashboard"
                    element={
                      <ProtectedRoute
                        requiredPermissions={["booking.authorize"]}
                      >
                        <AuthorizationDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="approval/booking"
                    element={
                      <ProtectedRoute
                        requiredPermissions={["booking.authorize"]}
                      >
                        <BookingAuthorize />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="approval/income"
                    element={
                      <ProtectedRoute requiredPermissions={["income.approve"]}>
                        <IncomeAuthorize />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="approval/hidden-income"
                    element={
                      <ProtectedRoute requiredPermissions={["income.approve"]}>
                        <HiddenIncomeAuthorize />
                      </ProtectedRoute>
                    }
                  />

                  {/* User Management */}
                  <Route
                    path="users"
                    element={
                      <ProtectedRoute requiredPermissions={["user.view"]}>
                        <SystemUsers />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="users/add"
                    element={
                      <ProtectedRoute requiredPermissions={["user.create"]}>
                        <AddSystemUser />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="auth/user"
                    element={
                      <ProtectedRoute requiredPermissions={["user.view"]}>
                        <SystemUsers />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="auth/user/add"
                    element={
                      <ProtectedRoute requiredPermissions={["user.create"]}>
                        <AddSystemUser />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="auth/user/view"
                    element={
                      <ProtectedRoute requiredPermissions={["user.view"]}>
                        <AddSystemUser />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="auth/user/edit"
                    element={
                      <ProtectedRoute requiredPermissions={["user.edit"]}>
                        <AddSystemUser />
                      </ProtectedRoute>
                    }
                  />

                  {/* Role Management */}
                  <Route
                    path="roles"
                    element={
                      <ProtectedRoute requiredPermissions={["role.view"]}>
                        <Roles />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="roles/add"
                    element={
                      <ProtectedRoute requiredPermissions={["role.create"]}>
                        <AddRole />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="roles/edit"
                    element={
                      <ProtectedRoute requiredPermissions={["role.edit"]}>
                        <EditRole />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="auth/roles"
                    element={
                      <ProtectedRoute requiredPermissions={["role.view"]}>
                        <Roles />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="auth/roles/add"
                    element={
                      <ProtectedRoute requiredPermissions={["role.create"]}>
                        <AddRole />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="auth/roles/view"
                    element={
                      <ProtectedRoute requiredPermissions={["role.view"]}>
                        <ViewRole />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="auth/roles/edit"
                    element={
                      <ProtectedRoute requiredPermissions={["role.edit"]}>
                        <EditRole />
                      </ProtectedRoute>
                    }
                  />

                  {/* Permission Management */}
                  <Route
                    path="permissions"
                    element={
                      <ProtectedRoute requiredPermissions={["role.view"]}>
                        <Permissions />
                      </ProtectedRoute>
                    }
                  />

                  {/* Reports */}
                  <Route
                    path="reports/unit"
                    element={
                      <ProtectedRoute requiredPermissions={["report.unit"]}>
                        <UnitReport />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="reports/income"
                    element={
                      <ProtectedRoute requiredPermissions={["report.income"]}>
                        <IncomeReport />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="reports/follow-up"
                    element={
                      <ProtectedRoute requiredPermissions={["report.followup"]}>
                        <FollowUpReport />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="reports/receivable"
                    element={
                      <ProtectedRoute requiredPermissions={["report.receivable"]}>
                        <ReceivableReport />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="reports/combined-income"
                    element={
                      <ProtectedRoute requiredPermissions={["report.combined"]}>
                        <CombinedIncomeReport />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="reports/booking"
                    element={
                      <ProtectedRoute requiredPermissions={["report.view"]}>
                        <div className="coming-soon">Booking Report - Coming Soon</div>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="reports/advanced-follow-up"
                    element={
                      <ProtectedRoute requiredPermissions={["report.advancedfollowup"]}>
                        <AdvancedFollowUpReport />
                      </ProtectedRoute>
                    }
                  />
                </Route>

                {/* Redirect any unknown routes to login if not authenticated, dashboard if authenticated */}
                <Route path="*" element={<Navigate to="/login" replace />} />
              </Routes>
            </div>
          </Router>
        </HiddenModeProvider>
      </SecurityProvider>
    </AuthProvider>
  );
}

export default App;
